<template>
<link v-if="isUmbraco" rel="stylesheet" href="/assets/index.css" />

  <section ref="scrollElement" :class="containerStyles">
    <div
      class="mt-20 mb-14 grid-cols-12 col-start-2 col-span-12 flex justify-between items-end md:mt-36"
    >
      <header2 v-if="title" :text="title" :is-umbraco="isUmbraco" :theme="themeType" />
      <link-button
        v-if="ctaLink && ctaLabel"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :theme="themeType"
        :is-umbraco="isUmbraco"
        type="secondary"
        classes="hidden md:inline-block"
      />
    </div>
    <div
      class="grid grid-cols-12 col-start-2 col-span-12 gap-y-10 md:gap-y-32 md:col-start-2 md:col-span-12 md:gap-x-12 2xl:gap-x-20"
    >
      <a
        v-for="(card, index) in newsCards"
        :class="cardStyles"
        :href="card.href ? card.href : card.url ? card.url : 'javascript:void(0)'"
        :target="card.target"
      >
        <news-card
          :title="card.title"
          :date="card.date"
          :tag="card.tag"
          :location="card.location"
          :image="card.image"
          :imgAlt="card.imgAlt"
          :isHorizontal="horizontalLayout"
          :is-umbraco="isUmbraco"
          :theme="theme"
          :index="index"
        />
      </a>
    </div>
    <div class="md:hidden col-start-2 col-span-12 mt-14 flex justify-center">
      <link-button
        v-if="ctaLink && ctaLabel"
        :label="ctaLabel"
        :url="ctaLink"
        :target="target"
        :theme="themeType"
        :is-umbraco="isUmbraco"
        type="secondary"
      />
    </div>
  </section>
</template>

<script lang="ts" setup>
import LinkButton from "@atoms/LinkButton/LinkButton.ce.vue";
import Header2 from "@components/Typography/Header2.ce.vue";
import NewsCard from "@molecules/NewsCard/NewsCard.ce.vue";
import { changeTheme, getThemeType, formatDate } from "@/util/helper";
import { onIntersect } from "@/composables/onIntersect";
import { computed, toRefs, onMounted, onUnmounted, ref, Ref, onBeforeMount } from "vue";
import { ILatestNewsProps } from "@/types/components/ILatestNewsProps";
import { getNews } from "@/util/api";

const observer: Ref<IntersectionObserver | null> = ref(null);
const scrollElement = ref(null);
const isLoading = ref(true);
const editorNewsItems = ref(0);
const dynamicNewsItems = ref(0);
const cardsArray = ref([]);

const props = withDefaults(defineProps<ILatestNewsProps>(), {
  isUmbraco: false,
  target: "_self",
  theme: "sand",
});

const { isUmbraco, theme, title, cards, ctaLabel, ctaLink, classes, isRelated } = toRefs(
  props
);

const onEnter = () => {
  if (!theme.value) return;
  changeTheme(theme.value);
};
const onExit = () => {};

onBeforeMount(() => {
  if (theme.value) {
    isLoading.value = false;
  }
});

onMounted(() => {
  if (cards?.value) {
    cardsArray.value = JSON.parse(cards!.value!);
  }
  editorNewsItems.value = cards?.value ? cardsArray.value.length : 0;

  if (editorNewsItems.value < 3 && isRelated.value !== true) {
    dynamicNewsItems.value = 3 - editorNewsItems.value;
    getDynamicNews(dynamicNewsItems.value);
  }

  if (!isLoading.value) {
    if (!scrollElement.value) return;
    observer.value = onIntersect(scrollElement.value, onEnter, onExit, false, {
      threshold: 0.6,
    });
  }
});

onUnmounted(() => {
  if (!observer.value) return;
  observer.value.disconnect();
});

const getDynamicNews = async (items: number) => {
  const globalNewsContainerId = document.documentElement.dataset.newscontainer!;
  const globalNewsLocationId = document.documentElement.dataset.newslocation!;
  const data = await getNews(globalNewsContainerId, globalNewsLocationId, 1, items);
  const newCards = data?.map((item) => {
    return {
      image: JSON.stringify(item.properties.image[0]),
      imgAlt: item.properties.imageAltText,
      location: item.properties.newsRegion[0].name,
      date: formatDate(item.properties.newsDate),
      title: item.properties.title,
      isHorizontal: true,
      href: item.route.path,
      theme: "sand",
    };
  });

  if (cardsArray.value.length === 0) {
    cards!.value = JSON.stringify(newCards);
  } else {
    const cardArray = JSON.parse(cards!.value!);
    const combiunedArray = [...cardArray, ...newCards];
    cards!.value = JSON.stringify(combiunedArray);
  }
};

const containerStyles = computed(() => {
  const bgColor = isUmbraco.value ? `bg-${theme.value}` : ``;
  return `${bgColor} sectionContainer place-content-start ${classes?.value} pb-40`;
});

const cardStyles = computed(() => {
  if (!cards?.value) return;
  const cardWidth =
    cards.value.length === 1
      ? ""
      : cards.value.length === 2
      ? "md:col-span-6"
      : "md:col-span-4";
  return `col-span-full ${cardWidth} flex flex-col group cursor-pointer`;
});

const horizontalLayout = computed(() => {
  if (!cards?.value) return false;
  return cards?.value.length === 1;
});

const themeType = computed(() => {
  if (!theme.value) return "dark";
  return getThemeType(theme.value);
});

const newsCards = computed(() => {
  if (!cards?.value) return [];
  return JSON.parse(cards.value);
});
</script>
